/**
 * Shared "Accept KRA / KPI" page. Renders admin-managed HTML, gates
 * the Continue button behind a tick + scroll-to-bottom check, and
 * posts to /api/auth/accept-<kind>.
 *
 * Usage:
 *   <AcceptTerms kind="kra" />
 *   <AcceptTerms kind="kpi" />
 *
 * After save, we refresh `user` from /me (so `onboardingStep` is fresh)
 * and route to the next onboarding step.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Target, CheckCircle2, ArrowRight, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth, routeForStep } from '../../store/auth';

const META = {
    kra: {
        icon:     FileText,
        title:    'Accept your Key Result Areas',
        subtitle: 'Please read through your KRAs carefully. You\'ll be evaluated against them at every quarterly review.',
        bodyKey:  'kraHtml',
        verKey:   'kraVersion',
        endpoint: '/auth/accept-kra',
        checkbox: 'I have read and agree to be evaluated against these KRAs.',
    },
    kpi: {
        icon:     Target,
        title:    'Accept your Key Performance Indicators',
        subtitle: 'Your monthly performance and incentive payouts are scored against these KPIs.',
        bodyKey:  'kpiHtml',
        verKey:   'kpiVersion',
        endpoint: '/auth/accept-kpi',
        checkbox: 'I have read and understand the KPI scoring methodology.',
    },
};

export default function AcceptTerms({ kind }) {
    const meta = META[kind];
    if (!meta) throw new Error(`Unknown AcceptTerms kind: ${kind}`);
    const Icon = meta.icon;

    const loadMe = useAuth(s => s.loadMe);
    const nav    = useNavigate();

    const [doc, setDoc]         = useState(null);
    const [accepted, setAccept] = useState(false);
    const [scrolled, setScroll] = useState(false);
    const [busy, setBusy]       = useState(false);
    const scrollRef = useRef(null);

    /* Fetch terms */
    useEffect(() => {
        let alive = true;
        api.get('/onboarding-content').then(r => { if (alive) setDoc(r.data); });
        return () => { alive = false; };
    }, []);

    /* Mark scrolled when user reaches the bottom of the long-form HTML. */
    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
        if (atBottom && !scrolled) setScroll(true);
    };

    /* If the content is short enough that there's nothing to scroll, unlock automatically. */
    useEffect(() => {
        const el = scrollRef.current;
        if (!doc || !el) return;
        if (el.scrollHeight <= el.clientHeight + 12) setScroll(true);
    }, [doc]);

    const submit = async () => {
        if (!accepted) return toast.error('Tick the checkbox to confirm');
        setBusy(true);
        try {
            await api.post(meta.endpoint, { accepted: true });
            toast.success('Saved ✓');
            const u = await loadMe();
            nav(routeForStep(u));
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    if (!doc) {
        return <div className="ag-card p-10 text-center text-sm text-ink-500">Loading terms…</div>;
    }

    const html    = doc[meta.bodyKey] || '<p>(no content)</p>';
    const version = doc[meta.verKey];

    return (
        <div className="space-y-6">
            <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center mx-auto">
                    <Icon className="h-7 w-7" />
                </div>
                <h1 className="text-2xl font-extrabold mt-4">{meta.title}</h1>
                <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">{meta.subtitle}</p>
                <span className="inline-flex items-center gap-1 mt-3 text-[10px] uppercase tracking-wider font-bold rounded-full bg-ink-100 text-ink-500 px-2.5 py-1">
                    Version {version}
                </span>
            </div>

            <div className="ag-card overflow-hidden">
                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    className="ag-onboard-html max-h-[420px] overflow-y-auto px-5 py-5 lg:px-8 lg:py-7"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
                {!scrolled && (
                    <div className="px-5 py-2 text-[11px] bg-amber-50 text-amber-700 border-t border-amber-100 inline-flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" /> Scroll to the bottom to enable the checkbox.
                    </div>
                )}
            </div>

            <label className={`flex items-start gap-3 p-4 rounded-xl border transition cursor-pointer
                ${scrolled ? 'border-brand-teal/40 bg-brand-teal-50/50 hover:bg-brand-teal-50'
                           : 'border-ink-200 bg-ink-50 opacity-60 cursor-not-allowed'}`}>
                <input
                    type="checkbox"
                    disabled={!scrolled}
                    checked={accepted}
                    onChange={e => setAccept(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-brand-teal"
                />
                <span className="text-sm font-medium text-brand-dark">{meta.checkbox}</span>
            </label>

            <button
                className="ag-btn-primary w-full justify-center"
                disabled={!accepted || busy}
                onClick={submit}
            >
                {busy ? 'Saving…' : <>I accept · Continue <ArrowRight className="h-4 w-4" /></>}
                {accepted && !busy && <CheckCircle2 className="h-4 w-4" />}
            </button>

            <p className="text-[11px] text-ink-500 text-center">
                Your acceptance is recorded with the current version. If terms change later, you'll be asked to re-accept.
            </p>
        </div>
    );
}
